/**
 * CollectionRenameModal — modal for renaming an existing collection.
 *
 * Design.md §13a.1:
 *   "long-press (mobile) or right-click (desktop) a collection chip →
 *    `Rename` · `Delete` (modal confirmation…)"
 *
 * Design invariants (design.md §2):
 *   - Name input is pre-filled with the current name.
 *   - Slug is server-derived — not surfaced to the user.
 *   - 409 conflict surfaces as inline form error, not a toast.
 *   - Standard accent (not destructive) for the Rename confirm button.
 *   - No exclamations, no banned words (branding.md §5.2.1).
 *
 * Accessibility: focus trap, ESC closes, role=dialog, aria-modal.
 * Mobile: bottom-sheet at ≤ 640px; centred sheet on tablet/desktop.
 */

import {
  useState,
  useEffect,
  useRef,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

// ─── Styles ───────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--scrim)",
  display: "flex",
  zIndex: 1000,
};

const SHEET_CSS = `
  @media (max-width: 639px) {
    .proof-col-rename-overlay {
      align-items: flex-end !important;
      justify-content: stretch !important;
    }
    .proof-col-rename-panel {
      border-radius: var(--radius-sheet-top) var(--radius-sheet-top) 0 0 !important;
      max-width: 100% !important;
      width: 100% !important;
    }
  }
  @media (min-width: 640px) {
    .proof-col-rename-overlay {
      align-items: center;
      justify-content: center;
    }
    .proof-col-rename-panel {
      width: 360px;
    }
  }
`;

const panelStyle: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "var(--radius-card)",
  // --line-strong (not --line): readable panel edge on --bg; see RecipeCreationModal.
  border: "1px solid var(--line-strong)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "var(--space-4) var(--space-4) var(--space-3)",
  borderBottom: "1px solid var(--line)",
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "14px",
  fontWeight: 500,
  color: "var(--text)",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  color: "var(--muted)",
  fontSize: "18px",
  lineHeight: 1,
  padding: "var(--space-1)",
  borderRadius: "4px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color var(--motion-state)",
};

const bodyStyle: React.CSSProperties = {
  padding: "var(--space-4)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
};

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--muted)",
  display: "block",
};

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: "100%",
    border: hasError ? "1px solid var(--del)" : "1px solid var(--line-strong)",
    borderRadius: "var(--radius-button)",
    background: "transparent",
    padding: "var(--space-2) var(--space-3)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--text)",
    outline: "none",
    minHeight: "var(--tap-target-min)",
    boxSizing: "border-box",
    transition: "border-color var(--motion-state)",
  };
}

const errorStyle: React.CSSProperties = {
  fontFamily: "var(--font-sans)",
  fontSize: "12px",
  color: "var(--del)",
};

const footerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: "var(--space-2)",
  justifyContent: "flex-end",
  padding: "var(--space-3) var(--space-4)",
  borderTop: "1px solid var(--line)",
  flexShrink: 0,
};

function cancelButtonStyle(): React.CSSProperties {
  return {
    background: "none",
    border: "1px solid var(--line)",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--muted)",
    cursor: "pointer",
    minHeight: "var(--tap-target-min)",
    transition: "color var(--motion-state), border-color var(--motion-state)",
  };
}

function renameButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    background: disabled ? "color-mix(in srgb, var(--accent) 50%, transparent)" : "var(--accent)",
    border: "none",
    borderRadius: "var(--radius-button)",
    padding: "var(--space-2) var(--space-4)",
    fontFamily: "var(--font-sans)",
    fontSize: "13px",
    color: "var(--surface)",
    cursor: disabled ? "not-allowed" : "pointer",
    boxShadow: disabled ? "none" : "var(--shadow-cta)",
    minHeight: "var(--tap-target-min)",
    transition: "opacity var(--motion-state), background var(--motion-state)",
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

const MAX_NAME_LENGTH = 60;

export interface CollectionRenameModalProps {
  /** UUID of the collection being renamed. */
  collectionUuid: string;
  /** Current name of the collection (pre-fills the input). */
  currentName: string;
  /**
   * Called when the user confirms. Returns a Promise that resolves on success
   * or rejects with an error (the modal handles 409 inline).
   */
  onRename: (uuid: string, newName: string) => Promise<void>;
  onClose: () => void;
}

export function CollectionRenameModal({
  collectionUuid,
  currentName,
  onRename,
  onClose,
}: CollectionRenameModalProps) {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Focus and select-all on mount.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // ESC closes the modal.
  useEffect(() => {
    function onKeyDown(e: globalThis.KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) onClose();
  }

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value.slice(0, MAX_NAME_LENGTH));
    if (error) setError(null);
  }

  function handleInputKeyDown(e: ReactKeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      void handleSubmit();
    }
  }

  async function handleSubmit(e?: FormEvent) {
    e?.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onRename(collectionUuid, trimmed);
      onClose();
    } catch (err: unknown) {
      const msg =
        err instanceof Error && err.message.toLowerCase().includes("slug_taken")
          ? "A collection with a similar name already exists."
          : "Could not rename the collection. Try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = name.trim().length > 0 && name.trim() !== currentName.trim() && !submitting;

  return (
    <>
      <style>{SHEET_CSS}</style>
      <div
        ref={overlayRef}
        className="proof-col-rename-overlay"
        style={overlayStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Rename collection"
        onClick={handleOverlayClick}
      >
        <div className="proof-col-rename-panel" style={panelStyle}>
          {/* Header */}
          <div style={headerStyle}>
            <span style={titleStyle}>Rename collection</span>
            <button type="button" style={closeButtonStyle} aria-label="Close" onClick={onClose}>
              ×
            </button>
          </div>

          {/* Body */}
          <form onSubmit={(e) => void handleSubmit(e)}>
            <div style={bodyStyle}>
              <label htmlFor="proof-col-rename-name" style={labelStyle}>
                Name
              </label>
              <input
                ref={inputRef}
                id="proof-col-rename-name"
                type="text"
                value={name}
                onChange={handleNameChange}
                onKeyDown={handleInputKeyDown}
                style={inputStyle(error !== null)}
                maxLength={MAX_NAME_LENGTH}
                autoComplete="off"
                disabled={submitting}
                aria-describedby={error ? "proof-col-rename-error" : undefined}
              />
              {error && (
                <span id="proof-col-rename-error" style={errorStyle} role="alert">
                  {error}
                </span>
              )}
            </div>

            {/* Footer */}
            <div style={footerStyle}>
              <button
                type="button"
                style={cancelButtonStyle()}
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" style={renameButtonStyle(!canSubmit)} disabled={!canSubmit}>
                Rename
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
